import { useEffect, useRef, useState, useCallback } from 'react';
import { API } from '../constants/api';
import { useAuth } from '../contexts/AuthContext';

export const useWebSocket = (conversationId) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState(null);
  const [lastMessage, setLastMessage] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const getToken = () => {
    // Try to get token from cookies
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'access_token') {
        return value;
      }
    }
    return null;
  };

  const connect = useCallback(() => {
    if (!user) return;

    const token = getToken();
    if (!token) {
      console.warn('No access token found for WebSocket connection');
      return;
    }

    try {
      // Convert http:// to ws:// or https:// to wss://
      // API is already /api, so we need to add /v1/chat/ws
      const baseUrl = API.replace('/api', '').replace(/^http/, 'ws');
      const wsUrl = baseUrl + '/api/v1/chat/ws?token=' + encodeURIComponent(token);
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;

        // Subscribe to conversation if provided
        if (conversationId) {
          ws.send(JSON.stringify({
            type: 'subscribe',
            conversation_ids: [conversationId],
          }));
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'connected') {
            console.log('WebSocket authenticated');
          } else if (data.type === 'new_message') {
            setLastMessage(event);
          } else if (data.type === 'error') {
            console.error('WebSocket error:', data.message);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
        setSocket(null);

        // Attempt to reconnect
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log(`Reconnecting... (attempt ${reconnectAttempts.current})`);
            connect();
          }, delay);
        } else {
          console.error('Max reconnection attempts reached');
        }
      };

      setSocket(ws);
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
    }
  }, [user, conversationId]);

  useEffect(() => {
    if (user) {
      connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (socket) {
        socket.close();
      }
    };
  }, [user, conversationId]);

  const sendMessage = useCallback((message) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message));
      return true;
    } else {
      console.warn('WebSocket is not connected');
      return false;
    }
  }, [socket]);

  return {
    socket,
    lastMessage,
    isConnected,
    sendMessage,
  };
};

